import { useEffect, useRef } from "react";

/**
 * Scroll-to-advance / scroll-to-retreat:
 *  - Keep scrolling DOWN at the true bottom of the current tab → next tab.
 *  - Keep scrolling UP at the true top of the current tab → previous tab.
 *
 * Anti-irritation rules (all deliberate — do not "simplify" them away):
 *  - Arms ONLY at the exact top/bottom (±2px). Reaching an edge alone does nothing.
 *  - Requires OVERSCROLL_PX (down) / OVERSCROLL_UP_PX (up) of ADDITIONAL wheel/touch
 *    input while at the edge — a visible progress bar fills so the user always sees
 *    what's about to happen. UP demands a little more because the top is where every
 *    tab starts (the resting position), so an accidental trigger is more likely there.
 *  - The bottom pill auto-surfaces when the user scrolls a scrollable tab to its end.
 *    The TOP pill NEVER auto-surfaces — every tab loads at the top, so it only appears
 *    once a deliberate upward overscroll starts charging it. Same for tabs that fit
 *    the viewport (no scroll room): both pills stay hidden until a gesture begins.
 *  - SETTLE_MS after landing at an edge is swallowed, so trackpad momentum / fling
 *    inertia can never charge a bar — only a fresh, intentional gesture.
 *  - Reversing direction or IDLE_RESET_MS of inactivity drains the bar.
 *  - Input in one direction always drains the opposite bar first.
 *  - Input inside nested scrollers (tables, lists) or fixed overlays (modals) is
 *    ignored — scrolling a modal must never flip the tab underneath it.
 *  - COOLDOWN_MS (shared by both directions) after any switch so tabs can never
 *    cascade or ping-pong between next/previous.
 *
 * Fully imperative: progress paints straight to the DOM (no per-frame React
 * re-renders of the huge BookingDetail tree).
 */

const BOTTOM_EPSILON = 2;      // px tolerance for "at the bottom" / "at the top"
const SETTLE_MS = 220;         // swallow momentum right after landing at an edge
const IDLE_RESET_MS = 900;     // no input for this long → progress drains to 0
const COOLDOWN_MS = 1100;      // after a switch, stay quiet (both directions)
const OVERSCROLL_PX = 300;     // deliberate extra scroll distance required (down)
const OVERSCROLL_UP_PX = 360;  // up asks slightly more — top is the resting position
const TOUCH_BOOST = 1.6;       // finger-swipes cover less distance than wheels

const useScrollTabAdvance = ({
  enabled, resetKey, anchorRef,
  pillRef, barRef,               // "next tab" pill (bottom of viewport)
  prevPillRef, prevBarRef,       // "previous tab" pill (top of viewport)
  containerOutRef,
  hasNext, hasPrev,
  onAdvance, onRetreat,
}) => {
  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;
  const onRetreatRef = useRef(onRetreat);
  onRetreatRef.current = onRetreat;
  // Cooldown must survive effect re-runs (tab change re-mounts the closure state).
  const cooldownUntilRef = useRef(0);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!enabled || !anchor) return undefined;

    // Nearest scrollable ancestor (the Layout content pane in this app).
    let sc = anchor.parentElement;
    while (sc && sc !== document.body) {
      const oy = getComputedStyle(sc).overflowY;
      if (oy === "auto" || oy === "scroll" || oy === "overlay") break;
      sc = sc.parentElement;
    }
    const container = sc && sc !== document.body ? sc : (document.scrollingElement || document.documentElement);
    if (containerOutRef) containerOutRef.current = container;
    const isDocScroller = container === document.scrollingElement || container === document.documentElement;
    const target = isDocScroller ? window : container;

    let progDown = 0;              // charge toward NEXT tab
    let progUp = 0;                // charge toward PREVIOUS tab
    let atBottom = false;
    let atTop = false;
    let bottomSince = 0;
    let topSince = 0;
    let lastInput = 0;
    let advancing = false;
    let idleTimer = 0;
    let touchY = null;

    const range = () => container.scrollHeight - container.clientHeight;
    const isAtBottom = () => range() - container.scrollTop <= BOTTOM_EPSILON;
    const isAtTop = () => container.scrollTop <= BOTTOM_EPSILON;
    // A tab that fits the viewport has no scroll room — it is inherently at BOTH edges.
    const hasScrollRoom = () => range() > BOTTOM_EPSILON;

    const paintDown = () => {
      if (barRef.current) barRef.current.style.transform = `scaleX(${progDown})`;
    };
    const paintUp = () => {
      if (prevBarRef && prevBarRef.current) prevBarRef.current.style.transform = `scaleX(${progUp})`;
    };

    const positionPill = (pill) => {
      if (!pill) return;
      if (isDocScroller) {
        pill.style.left = "50%";
      } else {
        const rect = container.getBoundingClientRect();
        pill.style.left = `${rect.left + rect.width / 2}px`;
      }
    };

    const setPill = (ref, show) => {
      const pill = ref && ref.current;
      if (!pill) return;
      if (show) positionPill(pill);
      pill.dataset.visible = show ? "true" : "false";
    };

    const drainDown = (hidePill) => {
      progDown = 0;
      paintDown();
      if (hidePill) setPill(pillRef, false);
    };
    const drainUp = (hidePill) => {
      progUp = 0;
      paintUp();
      if (hidePill) setPill(prevPillRef, false);
    };

    // Ignore input whose target sits inside a fixed overlay (modals) or another
    // scroller between it and our container (tables, inner lists, dropdowns).
    const isExempt = (t) => {
      let el = t instanceof Element ? t : null;
      while (el && el !== container && el !== document.body) {
        const cs = getComputedStyle(el);
        if (cs.position === "fixed") return true;
        const oy = cs.overflowY;
        if ((oy === "auto" || oy === "scroll" || oy === "overlay") && el.scrollHeight > el.clientHeight + 1) return true;
        el = el.parentElement;
      }
      return el !== container && !isDocScroller;
    };

    const evaluate = () => {
      const now = performance.now();
      const ready = !advancing && now >= cooldownUntilRef.current;
      const bNow = ready && !!hasNext && isAtBottom();
      const tNow = ready && !!hasPrev && isAtTop();
      if (bNow && !atBottom) bottomSince = now;
      if (tNow && !atTop) topSince = now;
      atBottom = bNow;
      atTop = tNow;
      if (!atBottom) drainDown(true);
      // Auto-surface the bottom hint only when the user actually scrolled a
      // scrollable tab to its end. On a tab that fits the viewport there is nothing
      // to scroll, so the pill stays hidden until a deliberate overscroll gesture
      // reveals it (accumulate → setPill), preventing a persistent nag.
      else if (hasScrollRoom()) setPill(pillRef, true);
      // The TOP hint never auto-surfaces — every tab loads at the top, so showing it
      // on arrival would nag on every single tab. A deliberate upward overscroll
      // gesture reveals it via accumulateUp instead.
      if (!atTop) drainUp(true);
    };

    const scheduleIdleCheck = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        if (performance.now() - lastInput < IDLE_RESET_MS) return;
        if (progDown > 0 && progDown < 1) drainDown(!hasScrollRoom());
        if (progUp > 0 && progUp < 1) drainUp(true); // top pill is gesture-only → always tuck it away
      }, IDLE_RESET_MS + 40);
    };

    const trigger = (dir) => {
      advancing = true;
      cooldownUntilRef.current = performance.now() + COOLDOWN_MS;
      drainDown(true);
      drainUp(true);
      // Safety valve: if the parent somehow doesn't re-render, un-stick later.
      window.setTimeout(() => { advancing = false; }, COOLDOWN_MS);
      const cb = dir === "prev" ? onRetreatRef.current : onAdvanceRef.current;
      cb && cb();
    };

    const accumulateDown = (dy, now) => {
      lastInput = now;
      setPill(pillRef, true); // reveal the hint as a deliberate overscroll begins
      progDown = Math.min(1, progDown + dy / OVERSCROLL_PX);
      paintDown();
      if (progDown >= 1) trigger("next");
      else scheduleIdleCheck();
    };

    const accumulateUp = (dy, now) => {
      lastInput = now;
      setPill(prevPillRef, true); // the top pill only ever appears through this gesture
      progUp = Math.min(1, progUp + dy / OVERSCROLL_UP_PX);
      paintUp();
      if (progUp >= 1) trigger("prev");
      else scheduleIdleCheck();
    };

    const onWheel = (e) => {
      if (advancing) return;
      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16;
      else if (e.deltaMode === 2) dy *= container.clientHeight;
      const now = performance.now();

      if (dy > 0) {
        // Downward intent → any "previous tab" charge is abandoned.
        if (progUp > 0) drainUp(true);
        if (!atBottom) { evaluate(); return; } // re-arm lazily (post-cooldown, no scroll event fires at the edge)
        if (isExempt(e.target)) return;
        if (now - bottomSince < SETTLE_MS) return; // momentum tail — not deliberate
        accumulateDown(dy, now);
      } else if (dy < 0) {
        // Upward intent → any "next tab" charge is abandoned.
        if (progDown > 0) drainDown(!hasScrollRoom());
        if (!atTop) { evaluate(); return; }
        if (isExempt(e.target)) return;
        if (now - topSince < SETTLE_MS) return;
        accumulateUp(-dy, now);
      }
    };

    const onTouchStart = (e) => { touchY = e.touches[0] ? e.touches[0].clientY : null; };
    const onTouchMove = (e) => {
      if (advancing || touchY == null) return;
      const y = e.touches[0] ? e.touches[0].clientY : touchY;
      const dy = touchY - y; // finger up = content down = positive
      touchY = y;
      const now = performance.now();

      if (dy > 0) {
        if (progUp > 0) drainUp(true);
        if (!atBottom) { evaluate(); return; }
        if (isExempt(e.target)) return;
        if (now - bottomSince < SETTLE_MS) return;
        accumulateDown(dy * TOUCH_BOOST, now);
      } else if (dy < 0) {
        if (progDown > 0) drainDown(!hasScrollRoom());
        if (!atTop) { evaluate(); return; }
        if (isExempt(e.target)) return;
        if (now - topSince < SETTLE_MS) return;
        accumulateUp(-dy * TOUCH_BOOST, now);
      }
    };

    const onScroll = () => evaluate();
    const onResize = () => {
      evaluate();
      if (atBottom && pillRef.current) positionPill(pillRef.current);
      if (atTop && prevPillRef && prevPillRef.current) positionPill(prevPillRef.current);
    };

    target.addEventListener("scroll", onScroll, { passive: true });
    target.addEventListener("wheel", onWheel, { passive: true });
    target.addEventListener("touchstart", onTouchStart, { passive: true });
    target.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("resize", onResize);
    evaluate();

    return () => {
      target.removeEventListener("scroll", onScroll);
      target.removeEventListener("wheel", onWheel);
      target.removeEventListener("touchstart", onTouchStart);
      target.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("resize", onResize);
      window.clearTimeout(idleTimer);
      drainDown(true);
      drainUp(true);
    };
  }, [enabled, resetKey]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useScrollTabAdvance;
